import React, { useEffect, useState } from 'react';
import PageContainer from 'src/components/container/PageContainer';
import Appointments from './Appointments';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
} from '@mui/material';
import { useMain } from '../hooks/useMain';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import DashboardCard2 from 'src/components/shared/DashboardCard2';
import SearchImg from '../../assets/search.svg';
import BigFilter from 'src/components/inputs/BigFilter';
import ClearIcon from '@mui/icons-material/Close';
import {
  setAllAppointmentData,
  setSearchFilter,
  setGenderFilter,
  setSourceFilter,
  setStatusFilter,
  setItemsPerPage,
  setCurrentPage,
  resetFilters
} from 'src/redux/slices/appointmentSlice';

const filterMenu = [
  { value: 'Walk In', label: 'Walk In' },
  { value: 'Existing', label: 'Existing' },
  { value: 'Phone Call', label: 'Phone Call' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'Web Appointment', label: 'Web Appointment' },
];

const filterStatusMenu = [
  { value: 'RESERVED', label: 'RESERVED' },
  { value: 'COMPLETE', label: 'COMPLETE' },
];

const filterGenderWise = [
  { value: 'Female', label: 'Female' },
  { value: 'Male', label: 'Male' },
];

const AllAppointments = () => {
  const dispatch = useDispatch();
  const [showLoader, setShowLoader] = useState(true);
  const [clear, setClear] = useState(false);

  const {
    displayedAppointments,
    pagination,
    filters,
  } = useSelector((state) => state.appointments);

  const { fetchAllAppointments, activeBranchId, deleteManyAppointment, setShowMessage } = useMain();

  const clearDataHandler = () => {
    dispatch(resetFilters());
    dispatch(setItemsPerPage(10));
    dispatch(setCurrentPage(1));
    if (clear) {
      getData();
      setClear(false);
    }
  };

  const getData = async () => {
    try {
      setShowLoader(true);
      const res = await fetchAllAppointments(activeBranchId);
      if (res.statusCode === 200) {
        dispatch(setAllAppointmentData(res?.data || []));
      }
    } catch (error) {
      setShowMessage({
        message: error.message || 'Something went wrong',
        messageType: 'error',
      });
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    if (activeBranchId) {
      setClear(true);
      getData();
    }

    // Reset filters when component unmounts
    return () => {
      dispatch(resetFilters());
    };
  }, [activeBranchId]);

  const handleLimitChange = (e) => {
    dispatch(setItemsPerPage(Number(e.target.value)));
  };

  const handlePageChange = (e, page) => {
    dispatch(setCurrentPage(page));
  };

  const handleSearchChange = (e) => {
    // Trim leading/trailing spaces
    dispatch(setSearchFilter(e.target.value.trimStart()));
  };

  // Runs when the user pastes text into the search box
  const handleSearchPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').trim();

    // If it looks like a phone number, clean it (remove spaces, dashes, brackets, +91)
    const isPhoneLike = /^[+\d\s\-()]+$/.test(pasted);
    const cleaned = isPhoneLike
      ? pasted.replace(/[\s\-()]/g, '').replace(/^\+?91(?=\d{10}$)/, '')
      : pasted;

    dispatch(setSearchFilter(cleaned));
  };


  // Handle filter changes from BigFilter
  const handleFilterChange = (selectedValues) => {
    // Separate the selected values into their respective categories
    const sources = selectedValues.filter(val =>
      filterMenu.some(opt => opt.value === val));
    const statuses = selectedValues.filter(val =>
      filterStatusMenu.some(opt => opt.value === val));
    const genders = selectedValues.filter(val =>
      filterGenderWise.some(opt => opt.value === val));

    // Dispatch each filter type to Redux
    dispatch(setSourceFilter(sources));
    dispatch(setStatusFilter(statuses));
    dispatch(setGenderFilter(genders));
  };

  // Combine all selected filters for BigFilter value prop
  const selectedFilters = [
    ...filters.sourceOfAppointment,
    ...filters.appointmentStatus,
    ...filters.gender
  ];

  return (
    <PageContainer title="Manage Appointments" description="this contains all Appointments">
      <DashboardCard2 title="Manage Appointments">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.7rem' }}>
          {/* Show per page - Left side */}
          <div>
            <InputLabel>Show per page</InputLabel>
            <Select
              style={{
                borderRadius: '7px',
                border: '0.5px solid #6174DD',
                padding: '4px 12px',
                background: '#FFF',
                height: '40px',
                width: '100%',
              }}
              value={pagination.itemsPerPage}
              onChange={handleLimitChange}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
               <MenuItem value={1000000}>All</MenuItem>
            </Select>
          </div>

          {/* Search and Filter - Right side with 0.8rem gap (Search first, then Filter) */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.8rem' }}>
            {/* Search */}
            <div style={{ width: '170px' }} onClick={() => document.getElementById('search-input').focus()}>
              <InputLabel>Search</InputLabel>
                  <div className="search-input" style={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 10, width: '100%' }}>
                  <img src={SearchImg} alt="" />
              <input
                id="search-input"
                type="text"
                placeholder="Search Clients..."
                value={filters.search || ''}
                onChange={handleSearchChange}
                onPaste={handleSearchPaste}
                style={{ marginLeft: '0.5rem' }}
              />
                {filters.search && (
                  <IconButton
                    onClick={() => dispatch(setSearchFilter(''))}
                    style={{ padding: 1 }}
                  >
                    <ClearIcon style={{
                      color: '#000',
                      marginRight: '-0.2rem',
                      marginTop: '0.1rem',
                      fontSize: '1.1rem'
                    }} />
                  </IconButton>
                )}
              </div>
            </div>

            {/* Filter */}
            <div style={{ marginTop: '2px' }}>
              <BigFilter
                options={[
                  { title: 'Source of Appointment', items: filterMenu },
                  { title: 'Appointment Status', items: filterStatusMenu },
                  { title: 'Gender', items: filterGenderWise }
                ]}
                value={selectedFilters}
                onChange={handleFilterChange}
                placeholder="Filters"
              />
            </div>
          </div>
        </div>

        {showLoader ? (
          <ShowLoader />
        ) : displayedAppointments.length > 0 ? (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={12}>
                <Appointments
                  data={displayedAppointments}
                  getData={getData}
                />
              </Grid>
            </Grid>
          </Box>
        ) : (
          'No data found'
        )}
        <Box display={'flex'} justifyContent={'flex-end'}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            color="primary"
            onChange={handlePageChange}
          />
        </Box>
      </DashboardCard2>
    </PageContainer>
  );
};

export default AllAppointments;

